import { useEffect, useState } from "react";
import { getSubscriptionStatus } from "../services/subscriptionService";

/**
 * What the signed-in member's tier unlocks, in one place.
 *
 * A tier now grants exactly one thing: a monthly prompt allowance. It no longer
 * decides what anyone may READ — system-generated forecasts are internal, so
 * the answer to "may I see those" is "are you an administrator", and it is the
 * same answer whatever anyone pays.
 *
 * `loading` matters: until the answer is known, callers should render neither
 * the admin view nor the redirect, or an administrator gets bounced on every
 * refresh.
 */
export const useTierAccess = () => {
  const [state, setState] = useState({
    loading: true,
    admin: false,
    tier: 0,
    canSeeSystemResearch: false,
    canGenerateResearch: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await getSubscriptionStatus();
        if (cancelled) return;
        setState({
          loading: false,
          admin: !!status?.admin,
          tier: status?.tier ?? 0,
          tierName: status?.tier_name,
          // The server sends sees_system_forecasts, which is admin and nothing
          // else. The old computation read system_symbol_slots, a field that no
          // longer exists — and `?? 0` would have made every account, including
          // an administrator's, look unentitled rather than obviously broken.
          canSeeSystemResearch: !!status?.sees_system_forecasts,
          // Anyone signed in may generate; the allowance is what bounds it.
          canGenerateResearch: true,
          promptUsage: status?.prompt_usage || null,
        });
      } catch {
        // Billing unreachable: assume the member view rather than the admin one.
        // Failing closed here shows a member their own research, which is what
        // they came for; failing open would show internal forecasts to someone
        // whose status we could not confirm.
        if (!cancelled) {
          setState((prev) => ({ ...prev, loading: false }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
};

export default useTierAccess;
